import React from 'react'
import { TagList } from './TagList'
import { FilterAccordion } from './FilterAccordion'
import { Brand, Category, Size } from '@/api'
import { genderOptions } from '../../constants'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'

interface SidebarProps {
  brand?: Brand
  categories?: Category[]
  sizes?: Size[]
  selectedGender: string
  selectedCategories: string[]
  selectedConditions: string[]
  selectedSizes: string[]
  keyword?: string
  onGenderChange: (gender: string) => void
  onCategoryChange: (categories: string[]) => void
  onConditionChange: (conditions: string[]) => void
  onSizeChange: (sizes: string[]) => void
  onKeywordChange?: (keyword: string) => void
}

export const Sidebar: React.FC<SidebarProps> = ({
  brand,
  categories,
  sizes,
  selectedGender,
  selectedCategories,
  selectedConditions,
  selectedSizes,
  keyword,
  onGenderChange,
  onCategoryChange,
  onConditionChange,
  onSizeChange,
  onKeywordChange,
}) => {
  // カテゴリーフィルターのオプション
  const allowedCategories = [
    'ダウンジャケット',
    'ライトウェイトダウンジャケット',
    'ベスト',
    'ウィンドジャケット',
    'レインジャケット',
  ]

  const categoryOptions =
    categories
      ?.filter(
        (category) => category.name && allowedCategories.includes(category.name)
      )
      .map((category) => ({
        id: category.id,
        label: category.name as string,
      })) || []

  // コンディションフィルターのオプション
  const conditionOptions =
    brand?.conditions
      ?.filter((condition) => condition.name)
      .map((condition) => ({
        id: condition.id,
        label: condition.name as string,
      })) || []

  // サイズフィルターのオプション
  const sizeOptions =
    sizes
      ?.filter((size) => size.name)
      .map((size) => ({
        id: size.id,
        label: size.name as string,
      })) || []

  const handleCategoryChange = (categoryId: string) => {
    const newCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId]
    onCategoryChange(newCategories)
  }

  const handleConditionChange = (conditionId: string) => {
    const newConditions = selectedConditions.includes(conditionId)
      ? selectedConditions.filter((id) => id !== conditionId)
      : [...selectedConditions, conditionId]
    onConditionChange(newConditions)
  }

  const handleSizeChange = (sizeId: string) => {
    const newSizes = selectedSizes.includes(sizeId)
      ? selectedSizes.filter((id) => id !== sizeId)
      : [...selectedSizes, sizeId]
    onSizeChange(newSizes)
  }

  return (
    <>
      <TagList
        selectedGender={selectedGender}
        selectedCategories={selectedCategories}
        selectedConditions={selectedConditions}
        selectedSizes={selectedSizes}
        brand={brand}
        categories={categories}
        sizes={sizes}
        genderOptions={genderOptions}
        onGenderChange={onGenderChange}
        onCategoryChange={onCategoryChange}
        onConditionChange={onConditionChange}
        onSizeChange={onSizeChange}
      />
      <div className="w-full px-4 sm:pl-[30px] sm:pr-[20px] pt-2 sm:pt-[10px] pb-4 sm:pb-[40px]">
        {/* 検索テキストボックス */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="キーワード検索"
              value={keyword || ''}
              onChange={(e) => onKeywordChange?.(e.target.value)}
              className="pl-10 h-12 border-gray-300 rounded-none focus:ring-0 focus:border-gray-400"
            />
          </div>
        </div>

        <FilterAccordion
          title="Gender"
          options={genderOptions}
          singleSelect={true}
          selectedValue={selectedGender}
          onSelectionChange={onGenderChange}
        />
        <FilterAccordion
          title="Category"
          options={categoryOptions}
          selectedValues={selectedCategories}
          onMultiSelectionChange={handleCategoryChange}
        />
        <FilterAccordion
          title="Size"
          options={sizeOptions}
          selectedValues={selectedSizes}
          onMultiSelectionChange={handleSizeChange}
        />
        <FilterAccordion
          title="Condition"
          options={conditionOptions}
          selectedValues={selectedConditions}
          onMultiSelectionChange={handleConditionChange}
        />
      </div>
    </>
  )
}
